import React, { useContext, useCallback, useState, useEffect } from "react";
import JSZip from "jszip";
import UiContext from "../../../contexts/UiContext";
import IAttachment from "../../../models/IAttachment";
import OwLabel from "../OwLabel/OwLabel";
import OwTitle from "../OwTitle/OwTitle";
import OwButton from "../OwButton/OwButton";
import useEmblaCarousel from "embla-carousel-react";
import "./ViewDeliveryProofModal.scss";

type Props = {
    orderName: string;
    signature?: IAttachment;
    photos?: IAttachment[];
};

export const ViewDeliveryProofModal = (props: Props) => {
    const uiContext = useContext(UiContext);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [isDownloading, setIsDownloading] = useState(false);

    const [emblaRef, emblaApi] = useEmblaCarousel({
        loop: true,
        align: "center",
    });

    const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
    const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

    useEffect(() => {
        if (!emblaApi) return;
        const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
        emblaApi.on("select", onSelect);
        onSelect();
        return () => {
            emblaApi.off("select", onSelect);
        };
    }, [emblaApi]);

    const closeModal = () => {
        uiContext.showModal(false);
    };

    // Extract signature URL from processedDetail or fallback to signedUrl
    const signatureUrl = props.signature?.processedDetail?.[0]?.signedUrls?.[0] || props.signature?.signedUrl;

    // Extract photo URLs
    const photoUrls = (props.photos || [])
        .flatMap((photo) => {
            const urls = photo.processedDetail?.[0]?.signedUrls;
            if (urls && urls.length > 0) {
                // Use the largest available image (last in array)
                return [urls[urls.length - 1]];
            }
            return photo.signedUrl ? [photo.signedUrl] : [];
        })
        .filter(Boolean) as string[];

    const hasSignature = !!signatureUrl;
    const hasPhotos = photoUrls.length > 0;

    const downloadAll = async () => {
        setIsDownloading(true);
        try {
            const zip = new JSZip();

            // Add signature to zip
            if (signatureUrl) {
                const response = await fetch(signatureUrl);
                const blob = await response.blob();
                zip.file("signature.png", blob);
            }

            // Add photos to zip
            for (let i = 0; i < photoUrls.length; i++) {
                const response = await fetch(photoUrls[i]);
                const blob = await response.blob();
                zip.file(`photo-${i + 1}.jpg`, blob);
            }

            // Generate and download zip
            const zipBlob = await zip.generateAsync({ type: "blob" });
            const downloadUrl = URL.createObjectURL(zipBlob);
            const link = document.createElement("a");
            link.href = downloadUrl;
            link.download = `${props.orderName}-Proof-of-Delivery.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(downloadUrl);
        } catch (err) {
            console.error("Download failed:", err);
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <div className="ViewDeliveryProofModal p-3">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-start">
                <div>
                    <OwTitle leftIconName="fas fa-file-signature" title="delivery_proof" tag="h3" />
                    <div className="order-name-subtitle">{props.orderName}</div>
                </div>
                <div className="header-buttons">
                    {(hasSignature || hasPhotos) && (
                        <OwButton
                            className="btn-outline-primary me-2"
                            leftIconClass={isDownloading ? "fas fa-spinner fa-spin" : "fas fa-download"}
                            title="download_all"
                            fn={downloadAll}
                            disabled={isDownloading}
                        />
                    )}
                    <OwButton className="btn-danger" centerIconClass="fas fa-times" fn={closeModal} />
                </div>
            </div>

            {/* Signature Section */}
            {hasSignature && (
                <div className="signature-section mt-4">
                    <div className="section-label">
                        <i className="fas fa-signature me-2" />
                        <OwLabel title="signature" />
                    </div>
                    <div className="signature-container">
                        <img src={signatureUrl} alt="Signature" className="signature-image" />
                    </div>
                </div>
            )}

            {/* Photos Section */}
            {hasPhotos && (
                <div className="photos-section mt-4">
                    <div className="section-label">
                        <i className="fas fa-camera me-2" />
                        <OwLabel title="photos" />
                        <span className="photo-count">({photoUrls.length})</span>
                    </div>
                    <div className="photos-carousel">
                        <div className="carousel-viewport" ref={emblaRef}>
                            <div className="carousel-container">
                                {photoUrls.map((url, index) => (
                                    <div key={index} className="carousel-slide">
                                        <img src={url} alt={`Photo ${index + 1}`} />
                                    </div>
                                ))}
                            </div>
                        </div>
                        {photoUrls.length > 1 && (
                            <>
                                <button className="carousel-prev" onClick={scrollPrev} type="button">
                                    <i className="fas fa-chevron-left" />
                                </button>
                                <button className="carousel-next" onClick={scrollNext} type="button">
                                    <i className="fas fa-chevron-right" />
                                </button>
                                <div className="carousel-dots">
                                    {photoUrls.map((_, index) => (
                                        <button
                                            key={index}
                                            type="button"
                                            className={`carousel-dot ${index === selectedIndex ? "active" : ""}`}
                                            onClick={() => emblaApi?.scrollTo(index)}
                                        />
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Empty state */}
            {!hasSignature && !hasPhotos && (
                <div className="no-proof mt-4">
                    <i className="fas fa-inbox fa-2x mb-2" />
                    <div>
                        <OwLabel title="no_delivery_proof_available" />
                    </div>
                </div>
            )}
        </div>
    );
};

export default ViewDeliveryProofModal;
